import type { EstadoDoItem } from "@foto/shared";

/**
 * Como cada estado do item se apresenta.
 *
 * Um lugar só: o selo do cartão, o texto do resumo e a decisão de qual ação
 * oferecer saem daqui. Espalhar isso pelas telas foi o que, no acervo antigo,
 * fez a mesma situação aparecer com dois nomes diferentes em duas telas.
 *
 * O tom segue a semântica do sistema: `projecao` é a máquina trabalhando,
 * `fixado` é estado consolidado, `descartar` é o que exige atenção, `quieto` é
 * o que está parado esperando decisão.
 */
export const ESTADOS: Record<
  EstadoDoItem,
  {
    /** No selo de um item, sozinho. */
    rotulo: string;
    /**
     * Ao lado de um número, no resumo.
     *
     * Existe porque o rótulo do selo não concorda quando conta: "14 pronta" e
     * "3 falhou" foi o que apareceu no primeiro lote real.
     */
    contagem: (n: number) => string;
    tom: "brasa" | "projecao" | "fixado" | "descartar" | "quieto";
    emCurso: boolean;
  }
> = {
  sem_saldo: { rotulo: "sem saldo", contagem: () => "sem saldo", tom: "quieto", emCurso: false },
  analisando: {
    rotulo: "analisando",
    contagem: () => "analisando",
    tom: "projecao",
    emCurso: true,
  },
  gerando: { rotulo: "gerando", contagem: () => "gerando", tom: "projecao", emCurso: true },
  conferindo: {
    rotulo: "conferindo",
    contagem: () => "conferindo",
    tom: "projecao",
    emCurso: true,
  },
  suspeito: { rotulo: "conferir", contagem: () => "a conferir", tom: "descartar", emCurso: false },
  ok: {
    rotulo: "pronta",
    contagem: (n) => (n === 1 ? "pronta" : "prontas"),
    tom: "brasa",
    emCurso: false,
  },
  aprovado: {
    rotulo: "aprovada",
    contagem: (n) => (n === 1 ? "aprovada" : "aprovadas"),
    tom: "fixado",
    emCurso: false,
  },
  // Decisão do operador, não defeito: por isso `quieto`, e não o tom de falha.
  recusado: {
    rotulo: "recusada",
    contagem: (n) => (n === 1 ? "recusada" : "recusadas"),
    tom: "quieto",
    emCurso: false,
  },
  // Como a recusada: o operador parou o lote, e nada aqui está quebrado.
  abortado: {
    rotulo: "abortada",
    contagem: (n) => (n === 1 ? "abortada" : "abortadas"),
    tom: "quieto",
    emCurso: false,
  },
  falhou: {
    rotulo: "falhou",
    contagem: (n) => (n === 1 ? "falhou" : "falharam"),
    tom: "descartar",
    emCurso: false,
  },
};

/** A ordem em que os itens aparecem: o que pede decisão primeiro. */
const PRIORIDADE: Record<EstadoDoItem, number> = {
  suspeito: 0,
  falhou: 1,
  ok: 2,
  gerando: 3,
  analisando: 4,
  conferindo: 5,
  sem_saldo: 6,
  aprovado: 7,
  recusado: 8,
  abortado: 9,
};

export function ordemDe(estado: EstadoDoItem): number {
  return PRIORIDADE[estado];
}
